/**
 * Term-Normalisierung für die Suche.
 *
 * Wird sowohl beim Bauen des Index (scripts/build-catalog.ts) als auch bei jeder
 * Suchanfrage verwendet. Beide Seiten MÜSSEN dieselbe Funktion benutzen — sonst
 * findet der Index Begriffe nicht wieder, die er selbst erzeugt hat.
 *
 * Deutsche Besonderheit: Auf dem Handy — erst recht mit Handschuhen — tippt niemand
 * Umlaute. "uberwachung", "ueberwachung" und "überwachung" müssen denselben Treffer
 * liefern. Dafür erzeugt `expandTerm` pro Token beide Umlautvarianten und legt sie
 * gemeinsam in den Index; die Suchanfrage läuft durch dieselbe Expansion und trifft
 * damit unabhängig von der Schreibweise.
 */

/** ä → ae, ö → oe, ü → ue, ß → ss (die orthografisch korrekte Umschrift). */
export function transliterateUmlauts(input: string): string {
  return input
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
}

/** ä → a, ö → o, ü → u, ß → ss (die Schreibweise, die beim schnellen Tippen entsteht). */
export function stripDiacritics(input: string): string {
  return input
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/** Kleinschreibung, Unicode-normalisiert, ohne umschließende Satzzeichen. */
export function baseNormalize(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFC')
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .trim()
}

/**
 * Erzeugt alle Indexvarianten eines Tokens. Gibt `null` zurück, wenn nichts
 * Durchsuchbares übrig bleibt — MiniSearch überspringt solche Terme.
 */
export function expandTerm(token: string): string[] | null {
  const base = baseNormalize(token)
  if (base.length === 0) return null

  const variants = new Set<string>([base, transliterateUmlauts(base), stripDiacritics(base)])
  // Bindestrich-Komposita zusätzlich in ihre Bestandteile zerlegen, damit
  // "fwdv-7" auch über "fwdv" gefunden wird.
  if (base.includes('-')) {
    for (const part of base.split('-')) {
      if (part.length >= 1) {
        variants.add(part)
        variants.add(transliterateUmlauts(part))
        variants.add(stripDiacritics(part))
      }
    }
  }
  return [...variants]
}

/**
 * Normalisiert ein einzelnes Zeichen. Kann leer ("Zeichen entfällt") oder länger als
 * ein Zeichen sein (ß → ss) — genau deshalb braucht die Fundstellenmarkierung eine
 * mitgeführte Abbildung und darf nicht mit Positionen rechnen.
 */
function normalizeChar(char: string): string {
  const lower = char.toLowerCase()
  if (lower === 'ß') return 'ss'
  const bare = lower.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  if (/\s/.test(bare)) return ' '
  if (!/[\p{L}\p{N}-]/u.test(bare)) return ''
  return bare
}

/**
 * Normalisiert einen Text und liefert zu jeder Stelle des Ergebnisses die zugehörige
 * Stelle im Original.
 *
 * Die Abbildung ist nötig, weil die Normalisierung die Zeichenanzahl verändert:
 * Satzzeichen fallen weg, Leerraum wird zusammengezogen, "ß" wird zu zwei Zeichen. Wer
 * eine im normalisierten Text gefundene Position ungeprüft auf das Original anwendet,
 * markiert in der Trefferliste die falsche Stelle.
 */
export function normalizeWithMap(input: string): { normalized: string; map: number[] } {
  let normalized = ''
  const map: number[] = []

  for (let index = 0; index < input.length; index++) {
    const produced = normalizeChar(input[index]!)
    for (const char of produced) {
      // Aufeinanderfolgenden Leerraum zusammenziehen, führenden ganz weglassen.
      if (char === ' ' && (normalized.length === 0 || normalized.endsWith(' '))) continue
      normalized += char
      map.push(index)
    }
  }

  // Abschließenden Leerraum entfernen — er würde nur zu Fehltreffern am Textende führen.
  while (normalized.endsWith(' ')) {
    normalized = normalized.slice(0, -1)
    map.pop()
  }

  return { normalized, map }
}

/**
 * Normalisiert einen ganzen Text zu einer vergleichbaren Form. Grundlage für die
 * Substring-Suche, die deutsche Komposita abfängt ("überwachung" in
 * "Atemschutzüberwachung") — dort, wo eine Token-Suche prinzipbedingt nicht greift.
 */
export function normalizeForSubstring(input: string): string {
  return normalizeWithMap(input).normalized
}
